import React, { useEffect, useState } from 'react';
import { Modal, Table, Checkbox, Tag, message } from 'antd';
import { apiRequest } from '../../../service/apiRequest';  // your API util
import { showSuccess, showError } from '../../../components/errorHandler';


interface TemplateItem {
  template_id: number;
  template_name: string;
  template_status: number;
  tom_id: string | null; // null means NOT mapped
}

interface Props {
  visible: boolean;
  onCancel: () => void;
  templateData: TemplateItem[];
  organizationId: string; // pass org id to link template
  onSaved: () => void; // callback after successful save
}

const TemplateOrgMapping: React.FC<Props> = ({
  visible,
  onCancel,
  templateData,
  organizationId,
  onSaved,
}) => {
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const mappedIds = templateData
      .filter((cls) => cls.tom_id !== null)
      .map((cls) => cls.template_id);
    setSelectedTemplateIds(mappedIds);
  }, [templateData]);

  const onCheckChange = (TemplateId: number, checked: boolean) => {
    setSelectedTemplateIds((prev) =>
      checked ? [...prev, TemplateId] : prev.filter((id) => id !== TemplateId)
    );
  };

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
    },
    {
      title: 'Status',
      dataIndex: 'template_status',
      key: 'template_status',
      render: (status: number) =>
        status === 1 ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
    },
    {
      title: 'Mapped',
      dataIndex: 'tom_id',
      key: 'mapped',
      render: (tom_id: string | null) =>
        tom_id ? (
          <Tag color="blue">Mapped</Tag>
        ) : (
          <Tag color="default">Not Mapped</Tag>
        ),
    },
    {
      title: 'Select',
      key: 'select',
      render: (_: any, record: TemplateItem) => (
        <Checkbox
          checked={selectedTemplateIds.includes(record.template_id)}
          onChange={(e) => onCheckChange(record.template_id, e.target.checked)}
        />
      ),
    },
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      // API call - replace URL & payload as needed
      await apiRequest({
        method: 'put',
        url: `/map-template-to-organization/${organizationId}`, // example endpoint
        data: { template_ids: selectedTemplateIds },
      });
      showSuccess('Template mapped successfully');
      onSaved(); // notify parent to refresh or close modal
      onCancel();
    } catch (error) {
      console.error(error);
      showError('Failed to save template mappings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Map Template to Organization"
      visible={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Save"
      confirmLoading={loading}
      width={600}
    >
      <Table
        rowKey="template_id"
        dataSource={templateData}
        columns={columns}
        pagination={false}
        scroll={{ y: 300 }}
      />
    </Modal>
  );
};

export default TemplateOrgMapping;
